import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useT, useSeo } from "@/lib/i18n";
import { pub } from "@/lib/api";
import { PageHead, HaloCard, ShareBar } from "@/components/bits";
import { useConsent } from "@/components/Layout";

function VideoCard({ v }) {
  const { t, tl } = useT();
  const [consent, save] = useConsent();
  const [play, setPlay] = useState(false);
  const C = tl("videoControls");
  const start = () => { if (!consent?.media) save({ ...(consent || {}), media: true }); setPlay(true); };
  return (
    <div className="card overflow-hidden">
      {play ? <iframe title={v.title} className="w-full aspect-video" src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}?autoplay=1&mute=1`} allow="autoplay; encrypted-media" allowFullScreen />
        : <button onClick={start} className="relative block w-full aspect-video bg-[var(--forest)]" aria-label={`${C[0]}: ${v.title}`} data-testid="video-play">{v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover opacity-80" loading="lazy" />}<span className="absolute inset-0 grid place-items-center text-[var(--ivory)] font-semibold">▶ {C[0]}</span></button>}
      <div className="p-5"><p className="font-semibold">{v.title_override || v.title}</p><p className="help mt-2">{t("videoConsent")}</p><a className="text-sm underline" href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noreferrer">{C[4]}</a></div>
    </div>
  );
}

export function Library() {
  const { t, tl, locale, lp } = useT();
  useSeo(null, tl("nav")[8]);
  const F = tl("filters");
  const cats = [null, "love", "intuition", "rituals", "reflections", "videos", "live"];
  const [filter, setFilter] = useState(0);
  const [arts, setArts] = useState([]);
  const [vids, setVids] = useState([]);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    pub.get("/public/articles", { params: { locale } }).then((r) => setArts(r.data.items));
    pub.get("/public/videos", { params: { locale } }).then((r) => setVids(r.data));
    pub.get("/public/events").then((r) => setEvents(r.data));
  }, [locale]);
  const shown = arts.filter((a) => filter === 0 || a.category === cats[filter]);
  return (
    <>
      <PageHead title={t("libH1")} lead={t("libIntro")} />
      <section className="wrap pb-20">
        <div className="flex flex-wrap gap-2 mb-10" role="group">{F.map((f, k) => <button key={f} className="chip" aria-pressed={filter === k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}>{f}</button>)}</div>
        {filter < 5 && (shown.length ? (
          <div className="grid md:grid-cols-3 gap-6">{shown.map((a) => (
            <HaloCard key={a.id} className="card overflow-hidden"><Link to={lp(`/library/${a.slug}`)} className="block" data-testid={`article-${a.slug}`}><img src={a.cover} alt="" aria-hidden="true" loading="lazy" className="w-full h-48 object-cover object-[center_30%]" /><div className="p-6"><p className="eyebrow">{t("editorial")}</p><h3>{a.title}</h3><p className="text-sm text-[var(--muted)] mt-2">{a.excerpt?.split("|")[0]}…</p></div></Link></HaloCard>))}</div>
        ) : <p>{t("emptyLang")}</p>)}
        {(filter === 0 || filter === 5) && <div className="mt-14"><h2 className="text-4xl mb-6">{F[5]}</h2>{vids.length ? <div className="grid md:grid-cols-3 gap-6">{vids.map((v) => <VideoCard key={v.youtube_id} v={v} />)}</div>
          : <p data-testid="videos-empty">{t("noVideos")} <a className="underline" href="https://www.youtube.com/@Ayahuasca_Angel" target="_blank" rel="noreferrer">@Ayahuasca_Angel</a></p>}</div>}
        {(filter === 0 || filter === 6) && <div className="mt-14"><h2 className="text-4xl mb-4">{F[6]}</h2>{events.length ? events.map((e) => <p key={e.id}>{e.title_i18n?.[locale]} · {new Date(e.start_utc).toLocaleString(locale)}</p>) : <p>{t("liveEmpty")}</p>}</div>}
      </section>
    </>
  );
}

export function Article() {
  const { slug } = useParams();
  const { t, locale, lp } = useT();
  const [a, setA] = useState(undefined);
  useSeo(null, a?.title);
  useEffect(() => { pub.get(`/public/articles/${slug}`, { params: { locale } }).then((r) => setA(r.data)).catch(() => setA(null)); }, [slug, locale]);
  if (a === null) return <section className="wrap section"><h1>{t("notFound")}</h1><Link to={lp("/library")} className="underline">←</Link></section>;
  if (!a) return <section className="wrap section">…</section>;
  return (
    <article>
      <PageHead eyebrow={t("editorial")} title={a.title} />
      <div className="wrap pb-20 grid gap-8 max-w-[760px]">
        <img src={a.cover} alt="" aria-hidden="true" className="rounded-3xl w-full object-cover aspect-[16/9]" />
        <div className="grid gap-5 text-lg leading-[1.8] prose-max" data-testid="article-body">{String(a.body || "").split("|").map((p, k) => <p key={k}>{p}</p>)}</div>
        {a.gallery?.length > 0 && <div className="columns-2 md:columns-3 gap-4" data-testid="article-gallery">{a.gallery.map((g) => <img key={g} src={g} alt="" loading="lazy" className="w-full rounded-2xl mb-4 break-inside-avoid" />)}</div>}
        {a.credit && <p className="help" data-testid="article-credit">{a.credit.split(" — ")[0]}{a.credit.includes("https://") && <> — <a className="underline" href={a.credit.slice(a.credit.indexOf("https://"))} target="_blank" rel="noopener noreferrer">source</a></>}</p>}
        <ShareBar path={`/library/${a.slug}`} text={a.title} />
      </div>
    </article>
  );
}
